import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Container,
    Stack,
    Text,
    Link,
    HStack,
    VStack,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {InfoCircleBlack, ConfirmationIcon} from '../../components/icons'
import Field from '../../components/field'
import PasswordRequirements from '../../components/forms/password-requirements'
import useUpdatePasswordFields from '../../components/forms/useUpdatePasswordFields'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {
    API_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE
} from '@salesforce/retail-react-app/app/constants'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {useBrand} from '../../contexts/brand-context'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

const ResetPasswordLanding = ({
    email,
    submitForm,
    setShouldShowHeading,
    isAccountDashboard = false,
    clickSignIn
}) => {
    const {isGarage} = useBrand()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const form = useForm()
    const {formatMessage} = useIntl()
    const fields = useUpdatePasswordFields({form})
    const password = form.watch('password')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()

    const [isSuccess, setIsSuccess] = useState(false)

    const submit = async (values) => {
        form.clearErrors()
        try {
            const token = await getTokenWhenReady()
            const response = await fetch(`${appOrigin}/process-password?siteId=${siteId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    token,
                    form: {
                        email,
                        otp: values.otp,
                        newpassword: values.password
                    }
                })
            })

            if (!response.ok) {
                throw new Error('Failed to call OTP service')
            }

            const passwordData = await response.json()

            if (!passwordData || !passwordData.success) {
                const errMsg = passwordData?.error_message || 'Failed to update password'
                throw new Error(errMsg)
            } else {
                setIsSuccess(true)
                setShouldShowHeading(false)
            }
        } catch (error) {
            let errorMessage = error.message
            let gtmMsg

            try {
                const errorData = await error.response?.json()
                errorMessage = errorData?.message || errorMessage
                gtmMsg = errorMessage
            } catch (_) {
                // Do nothing
            }

            if (errorMessage === 'invalid OTP') {
                errorMessage =
                    'Invalid password. Please try again or send a new temporary password.'
                form.setError('otp', {
                    type: 'manual',
                    message: isGarage
                        ? formatMessage({
                              defaultMessage:
                                  'Invalid password. Please try again or send a new temporary password.',
                              id: 'reset_password_form.error.invalid_otp.garage'
                          })
                        : formatMessage({
                              defaultMessage:
                                  'Invalid password. Please try again or send a new temporary password.',
                              id: 'reset_password_form.error.invalid_otp.dynamite'
                          })
                })
            } else if (/password.*policy/i.test(errorMessage)) {
                gtmMsg = 'Current and new passwords cannot be the same.'
                form.setError('password', {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Current and new passwords cannot be the same.',
                        id: 'reset_password_form.error.password_policy'
                    })
                })
            } else if (INVALID_TOKEN_ERROR.test(errorMessage)) {
                gtmMsg = INVALID_TOKEN_ERROR_MESSAGE.defaultMessage
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                })
            } else {
                gtmMsg = API_ERROR_MESSAGE.defaultMessage
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(API_ERROR_MESSAGE)
                })
            }
            // GTM Modification
            sendViewErrorEvent(gtmMsg)
        }
    }

    if (isSuccess) {
        return (
            <Stack spacing={{base: 6}}>
                {isAccountDashboard && (
                    <Text as="h1" textStyle="webH1">
                        <FormattedMessage
                            defaultMessage="Password Changed"
                            id="account_detail.title.password_changed"
                        />
                    </Text>
                )}
                <Stack gap={'12px'}>
                    <HStack spacing={2}>
                        <ConfirmationIcon width={'20px'} height={'20px'} />
                        <Text textStyle="bodyXsSMedium">
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Your password has been successfully changed."
                                    id="reset_password_form.info.password_changed.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Your password has been successfully changed."
                                    id="reset_password_form.info.password_changed.dynamite"
                                />
                            )}
                        </Text>
                    </HStack>
                    <Text
                        textStyle="bodyXsSRegular"
                    >
                        {isGarage ? (
                            <FormattedMessage
                                defaultMessage="Please use your new password to log in!"
                                id="reset_password_form.info.new_password_log_in.garage"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Please use your new password to log in!"
                                id="reset_password_form.info.new_password_log_in.dynamite"
                            />
                        )}
                    </Text>
                </Stack>

                <Button
                    type="button"
                    variant="webPrimary"
                    onClick={clickSignIn}
                    w={{base: 'full', lg: '225px'}}
                    mt={{base: '24px', lg: '0'}}
                >
                    <FormattedMessage
                        defaultMessage="Sign In"
                        id="reset_password_form.action.sign_in"
                    />
                </Button>
            </Stack>
        )
    }
    return (
        <Stack spacing={4}>
            <Flex flexDirection={'column'} gap={'48px'}>
                {isAccountDashboard && (
                    <Text as="h1" textStyle="webH1">
                        <FormattedMessage
                            defaultMessage="Change Password"
                            id="account_detail.title.change_password"
                        />
                    </Text>
                )}
                <HStack spacing={2}>
                    <InfoCircleBlack boxSize={'16px'} />
                    <Text textStyle="bodyXsSRegular">
                        {isGarage ? (
                            <FormattedMessage
                                defaultMessage="Check your inbox!"
                                id="reset_password_page.check_inbox.garage"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Check your inbox!"
                                id="reset_password_page.check_inbox.dynamite"
                            />
                        )}
                    </Text>
                </HStack>
            </Flex>

            <VStack gap={'20px'} align="left">
                <Text textStyle="bodyXsSRegular">
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="Check your email that we sent to <b>{email}</b>"
                            id="reset_password_page.email_sent.text.garage"
                            values={{
                                email,
                                b: (chunks) => <b>{chunks}</b>
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Check your email that we sent to <b>{email}</b>"
                            id="reset_password_page.email_sent.text.dynamite"
                            values={{
                                email,
                                b: (chunks) => <b>{chunks}</b>
                            }}
                        />
                    )}
                </Text>

                <Text textStyle="bodyXsSRegular">
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="If you don’t receive an email within 5 minutes, check your junk folder or contact <a>Customer Service</a>."
                            id="reset_password_page.email_sent.help.garage"
                            values={{
                                a: (chunks) => (
                                    <Link
                                        href={getSiteEnvVarValue(appConfig, 'CUSTOMER_SERVICE_URL', siteId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="If you don’t receive an email within 5 minutes, check your junk folder or contact <a>Customer Service</a>."
                            id="reset_password_page.email_sent.help.dynamite"
                            values={{
                                a: (chunks) => (
                                    <Link
                                        href={getSiteEnvVarValue(appConfig, 'CUSTOMER_SERVICE_URL', siteId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }}
                        />
                    )}
                </Text>

                <Text textStyle="bodyXsSRegular">
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="Didn’t receive it? <a>Send me a new one</a>"
                            id="reset_password_page.email_sent.retry.garage"
                            values={{
                                a: (chunks) => (
                                    <Link
                                        as="button"
                                        onClick={() => submitForm?.({email})}
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Didn’t receive it? <a>Send me a new one</a>"
                            id="reset_password_page.email_sent.retry.dynamite"
                            values={{
                                a: (chunks) => (
                                    <Link
                                        as="button"
                                        onClick={() => submitForm?.({email})}
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }}
                        />
                    )}
                </Text>
            </VStack>
            <Stack justify="center" align="center" spacing={6}>
                <Container variant="form" mt={2}>
                    <form onSubmit={form.handleSubmit(submit)} noValidate>
                        <Stack spacing={4}>
                            {form.formState.errors?.global && (
                                <Alert data-testid="password-update-error" status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text fontSize="sm" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}
                            <Stack spacing={2} pb={2}>
                                <Field {...fields.otp} isResetPassword={true} />
                                <Field {...fields.password} isResetPassword={true} />
                                <PasswordRequirements value={password} />
                                <Field {...fields.passwordConfirm} isResetPassword={true} />
                            </Stack>
                            <Button
                                type="submit"
                                variant="webPrimary"
                                isLoading={form.formState.isSubmitting}
                                w={{base: 'full', lg: '225px'}}
                                isDisabled={
                                    !form.watch('otp') ||
                                    !form.watch('password') ||
                                    !form.watch('passwordConfirm')
                                }
                            >
                                <FormattedMessage
                                    defaultMessage="Set Password"
                                    id="reset_password_form.button.set_password"
                                />
                            </Button>
                            {!isAccountDashboard && (
                                <HStack>
                                    <Text textStyle="bodyXsSRegular">
                                        <FormattedMessage
                                            defaultMessage="Or return to"
                                            id="reset_password_form.message.return_to_sign_in"
                                            description="Precedes link to return to sign in"
                                        />
                                    </Text>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={
                                            clickSignIn
                                                ? clickSignIn
                                                : () => () => history.push('/login')
                                        }
                                    >
                                        <FormattedMessage
                                            defaultMessage="Sign in"
                                            id="reset_password_form.action.sign_in_page"
                                        />
                                    </Button>
                                </HStack>
                            )}
                        </Stack>
                    </form>
                </Container>
            </Stack>
        </Stack>
    )
}

ResetPasswordLanding.getTemplateName = () => 'reset-password-landing'

ResetPasswordLanding.propTypes = {
    email: PropTypes.string,
    submitForm: PropTypes.func,
    setShouldShowHeading: PropTypes.func,
    isAccountDashboard: PropTypes.bool,
    clickSignIn: PropTypes.func
}

export default ResetPasswordLanding
